import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  LayoutDashboard,
  Landmark,
  ArrowLeftRight,
  Upload,
  Tags,
  WalletCards,
  Repeat2,
  ChartNoAxesCombined,
  Settings,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  PlusCircle,
  Eye,
  EyeOff,
  Layers3,
  ChevronDown,
  ChevronRight,
  ScanSearch,
  Globe2,
  CircleHelp,
} from "lucide-react";
import { api, money } from "./lib/api.js";
import { t, supportedLocales, setTranslationOverrides } from "./lib/i18n.js";
import { installUiLocalization } from "./lib/uiPhraseTranslations.js";
import Dashboard from "./pages/Dashboard.jsx";
import Profile from "./pages/Profile.jsx";
import AccountDetail from "./pages/AccountDetail.jsx";
import Accounts from "./pages/Accounts.jsx";
import Transactions from "./pages/Transactions.jsx";
import ImportPage from "./pages/ImportPage.jsx";
import Config from "./pages/Config.jsx";
import RulesPage from "./pages/RulesPage.jsx";
import Planning from "./pages/Planning.jsx";
import Subscriptions from "./pages/Subscriptions.jsx";
import Investments from "./pages/Investments.jsx";
import Audit from "./pages/Audit.jsx";
import Organize from "./pages/Organize.jsx";
import MovementModal from "./components/MovementModal.jsx";
import CurrencySelect from "./components/CurrencySelect.jsx";
import MatchReview from "./pages/MatchReview.jsx";
import Help from "./pages/Help.jsx";
import "./styles.css";
import "./styles-v1.28.css";
const navGroups = [
  [
    "Money",
    [
      ["/dashboard", "Dashboard", LayoutDashboard, "S0001"],
      ["/accounts", "Accounts", Landmark, "S0100"],
      ["/movements", "Movements", ArrowLeftRight, "S0200"],
      ["/investments", "Holdings, assets & liabilities", ChartNoAxesCombined, "S0700"],
      ["/review", "Duplicates & transfers", ScanSearch, "S0250"],
      ["/import", "Import & Export", Upload, "S0300"],
    ],
  ],
  [
    "Organize",
    [
      ["/organize", "Classifications & structure", Layers3, "S0450"],
      ["/rules", "Rules", Tags, "S0400"],
    ],
  ],
  [
    "Plan",
    [
      ["/planning", "Budgets, goals & projects", WalletCards, "S0500"],
      ["/subscriptions", "Recurring payments", Repeat2, "S0600"],
    ],
  ],
  [
    "System",
    [
      ["/config", "Configuration", Settings, "S0800"],
      ["/help", "Help", CircleHelp, "S0850"],
      ["/audit", "Audit", ShieldCheck, "S0900"],
    ],
  ],
];
const nav = navGroups.flatMap((x) => x[1]);
const navLabelKeys = {
  Dashboard: "dashboard",
  Accounts: "accounts",
  Movements: "movements",
  "Import & Export": "importExport",
  "Classifications & structure": "classificationsStructure",
  Rules: "rules",
  "Budgets, goals & projects": "budgetsGoalsProjects",
  Subscriptions: "subscriptions",
  "Recurring payments": "subscriptions",
  "Assets & liabilities": "assetsLiabilities",
  "Holdings, assets & liabilities": "holdingsAssetsLiabilities",
  Configuration: "configuration",
  Help: "help",
  Audit: "audit",
};
const navGroupKeys = {
  Money: "money",
  Organize: "organize",
  Plan: "plan",
  System: "system",
};

const reportClientError = (error, componentStack = "") => {
  let actionTrail = [];
  try {
    actionTrail = JSON.parse(
      sessionStorage.getItem("pisto-action-trail") || "[]",
    );
  } catch {}
  const payload = {
    message: error?.message || String(error || "Unknown client error"),
    stack: error?.stack || "",
    componentStack,
    path: window.location.pathname,
    actionTrail,
    build: "1.28.0",
    userAgent: navigator.userAgent,
    at: new Date().toISOString(),
  };
  try {
    localStorage.setItem("pisto-last-client-error", JSON.stringify(payload));
  } catch {}
  try {
    fetch("/api/client-errors", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {}
};
class ErrorBoundary extends React.Component {
  constructor(p) {
    super(p);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    reportClientError(error, info?.componentStack || "");
    console.error("Pisto UI error", error, info);
  }
  render() {
    if (this.state.error)
      return (
        <div className="card route-error">
          <h2>This screen could not be displayed</h2>
          <p className="muted">
            {this.state.error.message || "Unexpected interface error."}
          </p>
          <div className="actions">
            <button
              className="primary"
              onClick={() => this.setState({ error: null })}
            >
              Try again
            </button>
            <button
              className="secondary"
              onClick={() => window.location.reload()}
            >
              Reload Pisto
            </button>
          </div>
        </div>
      );
    return this.props.children;
  }
}
function Setup({ done }) {
  const [f, setF] = useState({
      name: "Emiliano Lucero",
      email: "emilucero@gmail.com",
      password: "",
      baseCurrency: "EUR",
      locale: "en",
    }),
    [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      setErr("");
      await api("/setup/install", { method: "POST", body: f });
      done();
    } catch (e) {
      setErr(e.message);
    }
  };
  return (
    <div className="auth-page">
      <div className="setup-card">
        <div className="brand big">
          <span>P</span>Pisto
        </div>
        <p className="eyebrow">FIRST RUN</p>
        <h1>Set up Pisto</h1>
        <p className="muted">
          Creates the super-admin and secure application structure.
        </p>
        <form onSubmit={submit} className="form-grid">
          <label>
            Your name
            <input
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
            />
          </label>
          <label className="span2">
            Admin password
            <input
              type="password"
              minLength="10"
              value={f.password}
              onChange={(e) => setF({ ...f, password: e.target.value })}
            />
          </label>
          <label>
            Main currency
            <CurrencySelect
              required
              value={f.baseCurrency}
              onChange={(baseCurrency) => setF({ ...f, baseCurrency })}
            />
          </label>
          <label>
            Language
            <select
              value={f.locale}
              onChange={(e) => setF({ ...f, locale: e.target.value })}
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="pt">Português</option>
            </select>
          </label>
          {err && <div className="error span2">{err}</div>}
          <button className="primary span2">Install Pisto</button>
        </form>
      </div>
    </div>
  );
}
function Login({ done }) {
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({
      name: "",
      email: "emilucero@gmail.com",
      password: "",
      code: "",
      baseCurrency: "EUR",
    }),
    [err, setErr] = useState(""),
    [gid, setGid] = useState("");
  useEffect(() => {
    api("/auth/google-config")
      .then((x) => setGid(x.clientId))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!gid || mode !== "login") return;
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: gid,
        callback: async (r) => {
          try {
            await api("/auth/google", {
              method: "POST",
              body: { credential: r.credential },
            });
            done();
          } catch (e) {
            setErr(e.message);
          }
        },
      });
      window.google?.accounts.id.renderButton(
        document.getElementById("google-btn"),
        { theme: "outline", size: "large", width: 330 },
      );
    };
    document.head.appendChild(s);
    return () => s.remove();
  }, [gid, mode]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      setErr("");
      if (mode === "login")
        await api("/auth/login", {
          method: "POST",
          body: { email: f.email, password: f.password },
        });
      else await api("/auth/signup", { method: "POST", body: f });
      done();
    } catch (e) {
      setErr(e.message);
    }
  };
  return (
    <div className="auth-page">
      <div className="login-card">
        <div className="brand big">
          <span>P</span>Pisto
        </div>
        <h1>
          {mode === "login" ? "Welcome back" : "Create your Pisto account"}
        </h1>
        <p className="muted">
          {mode === "login"
            ? "Your money, assets and cash flow in one place."
            : "You need the one-time account creation code from a Pisto administrator."}
        </p>
        <form onSubmit={submit}>
          {mode === "signup" && (
            <label>
              Name
              <input
                required
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
              />
            </label>
          )}
          <label>
            Email
            <input
              type="email"
              required
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              minLength="10"
              required
              value={f.password}
              onChange={(e) => setF({ ...f, password: e.target.value })}
            />
          </label>
          {mode === "signup" && (
            <>
              <label>
                Account creation code
                <input
                  required
                  value={f.code}
                  onChange={(e) => setF({ ...f, code: e.target.value })}
                />
              </label>
              <label>
                Reporting currency
                <CurrencySelect
                  value={f.baseCurrency}
                  onChange={(baseCurrency) => setF({ ...f, baseCurrency })}
                />
              </label>
            </>
          )}
          {err && <div className="error">{err}</div>}
          <button className="primary full">
            {mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
        {mode === "login" && gid && (
          <>
            <div className="or">or</div>
            <div id="google-btn" className="google"></div>
          </>
        )}
        <button
          className="text-button auth-switch"
          onClick={() => {
            setErr("");
            setMode(mode === "login" ? "signup" : "login");
          }}
        >
          {mode === "login" ? "Create account" : "Back to sign in"}
        </button>
      </div>
    </div>
  );
}
function Shell({ user, setUser }) {
  const [open, setOpen] = useState(false),
    [movementOpen, setMovementOpen] = useState(false),
    [movementContext, setMovementContext] = useState({}),
    [debugEnabled, setDebugEnabled] = useState(false),
    [hideValues, setHideValues] = useState(false),
    [headerPrefs, setHeaderPrefs] = useState({}),
    [headerData, setHeaderData] = useState({}),
    [translationRevision, setTranslationRevision] = useState(0),
    [uiIssue, setUiIssue] = useState(""),
    [collapsedNav, setCollapsedNav] = useState(() => {
      try {
        return JSON.parse(localStorage.getItem("pisto-nav-collapsed") || "{}");
      } catch {
        return {};
      }
    });
  const loc = useLocation(),
    navg = useNavigate();
  const isAdmin = ["admin", "super_admin"].includes(user.role);
  useEffect(() => installUiLocalization(user.locale), [user.locale, translationRevision]);
  useEffect(() => {
    // These authenticated tasks belong in the signed-in shell, not the login screen.
    // The server cache prevents every user login from calling the free provider again.
    Promise.allSettled([
      api("/market-prices/refresh-owned", { method: "POST" }),
      api("/assets/recognize-dividends", { method: "POST" }),
    ]).then(() => window.dispatchEvent(new Event("pisto:data-changed")));
  }, [user.id]);
  useEffect(() => {
    api("/localization")
      .then((rows) => {
        setTranslationOverrides(rows);
        setTranslationRevision((x) => x + 1);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
    setUiIssue("");
  }, [loc.pathname]);
  useEffect(() => {
    const err = (e) => {
      const cause = e?.reason || e?.error || e;
      const m =
        cause?.message || e?.message || "A background interface task failed.";
      reportClientError(cause);
      setUiIssue(m);
    };
    window.addEventListener("unhandledrejection", err);
    window.addEventListener("error", err);
    return () => {
      window.removeEventListener("unhandledrejection", err);
      window.removeEventListener("error", err);
    };
  }, []);
  useEffect(() => {
    Promise.all([
      api("/config"),
      api("/dashboard?period=month").catch(() => null),
      api("/fx/latest").catch(() => null),
      api("/accounts").catch(() => []),
    ])
      .then(([c, d, fx, accounts]) => {
        setDebugEnabled(Boolean(c.preferences?.debug_enabled));
        localStorage.setItem(
          "pisto-debug-enabled",
          c.preferences?.debug_enabled ? "1" : "0",
        );
        localStorage.setItem("pisto-currency-display-mode", c.preferences?.currency_display_mode || "symbol");
        try {
          setHeaderPrefs(
            typeof c.preferences?.header_widgets_json === "string"
              ? JSON.parse(c.preferences.header_widgets_json || "{}")
              : c.preferences?.header_widgets_json || {},
          );
        } catch {
          setHeaderPrefs({});
        }
        setHeaderData({
          dashboard: d,
          fx,
          accounts,
          prefs: c.preferences || {},
        });
        const v = Boolean(c.preferences?.hide_values_default);
        localStorage.setItem("pisto-hide-values-runtime", v ? "1" : "0");
        setHideValues(v);
      })
      .catch(() => {});
  }, [loc.pathname]);
  const logout = async () => {
      await api("/auth/logout", { method: "POST" });
      setUser(null);
      navg("/");
    },
    go = (p) => {
      navg(p);
      setOpen(false);
    },
    contextFromRoute = () => {
      const m = loc.pathname.match(/^\/accounts\/(\d+)/);
      return m ? { accountId: Number(m[1]), source: "account" } : {};
    },
    openMovement = (context = {}) => {
      setMovementContext({ ...contextFromRoute(), ...context });
      setMovementOpen(true);
      setOpen(false);
    };
  const route = nav.find(
      (x) => loc.pathname === x[0] || loc.pathname.startsWith(x[0] + "/"),
    ),
    screenId =
      loc.pathname === "/profile"
        ? "S0810"
        : loc.pathname.match(/^\/accounts\/\d+/)
          ? "S0110"
          : route?.[3] || "S0000",
    headerScreen =
      loc.pathname === "/profile"
        ? "profile"
        : loc.pathname.match(/^\/accounts\/\d+/)
          ? "account_detail"
          : (route?.[0] || "/dashboard").replace("/", "") || "dashboard",
    widgetKeys = (headerPrefs?.[headerScreen] || []).slice(0, 3);
  const fxPair = () => {
    const fx = headerData.fx,
      p = headerData.prefs || {},
      base = p.fx_base_currency || "EUR",
      quote = p.fx_quote_currency || "USD",
      rate = (c) =>
        c === "EUR"
          ? 1
          : Number(fx?.rates?.find((x) => x.currency === c)?.eur_rate || 0);
    const b = rate(base),
      q = rate(quote);
    return b && q
      ? [`${base} / ${quote}`, (q / b).toFixed(4)]
      : [`${base} / ${quote}`, "—"];
  };
  const widget = (key) => {
    const d = headerData.dashboard,
      accounts = headerData.accounts || [];
    if (key === "cash_balance")
      return ["Cash", d ? money(d.totalBalance, d.baseCurrency) : "—"];
    if (key === "net_worth")
      return ["Net worth", d ? money(d.netWorth, d.baseCurrency) : "—"];
    if (key === "passive_ytd")
      return [
        "Passive YTD",
        d ? money(d.passiveIncomeYtd, d.baseCurrency) : "—",
      ];
    if (key === "account_count") return ["Accounts", String(accounts.length)];
    if (key === "fx_pair" || key === "fx_usd") return fxPair();
    return [key, "—"];
  };
  return (
    <div className="shell">
      <aside className={open ? "sidebar open" : "sidebar"}>
        <div className="side-head">
          <div className="brand">
            <span>P</span>Pisto
          </div>
          <button className="icon mobile" onClick={() => setOpen(false)}>
            <X />
          </button>
        </div>
        <button className="side-add" onClick={() => openMovement()}>
          <PlusCircle size={18} /> {t(user.locale, "addMovement")}
        </button>
        <nav>
          {navGroups.map(([group, items]) => {
            const collapsed = !!collapsedNav[group],
              visibleItems = items.filter(
                ([path]) => path !== "/audit" || isAdmin,
              );
            return (
              <div className="nav-group" key={group}>
                <button
                  type="button"
                  className="nav-group-toggle"
                  title={t(
                    user.locale,
                    collapsed ? "expandSection" : "collapseSection",
                  )}
                  onClick={() =>
                    setCollapsedNav((prev) => {
                      const next = { ...prev, [group]: !prev[group] };
                      localStorage.setItem(
                        "pisto-nav-collapsed",
                        JSON.stringify(next),
                      );
                      return next;
                    })
                  }
                >
                  <small>{t(user.locale, navGroupKeys[group] || group)}</small>
                  {collapsed ? (
                    <ChevronRight size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </button>
                {!collapsed &&
                  visibleItems.map(([p, n, I]) => (
                    <button
                      key={p}
                      type="button"
                      className={
                        loc.pathname === p || loc.pathname.startsWith(p + "/")
                          ? "active"
                          : ""
                      }
                      onClick={() => go(p)}
                    >
                      <I size={19} />
                      <span>{t(user.locale, navLabelKeys[n] || n)}</span>
                    </button>
                  ))}
              </div>
            );
          })}
        </nav>
        <div className="userbox">
          <button
            className="userbox-profile"
            type="button"
            onClick={() => go("/profile")}
          >
            <div className="avatar">
              {user.avatar_data ? (
                <img src={user.avatar_data} alt="" />
              ) : (
                user.name?.[0] || "P"
              )}
            </div>
            <div>
              <strong>{user.name}</strong>
              <small>{user.role?.replace("_", " ")}</small>
            </div>
          </button>
          <button
            className="icon"
            title={t(user.locale, "signOut")}
            onClick={logout}
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>
      <main>
        <header className="topbar">
          <button className="icon mobile" onClick={() => setOpen(true)}>
            <Menu />
          </button>
          <div className="topbar-title">
            <strong>
              {loc.pathname === "/profile"
                ? t(user.locale, "profile")
                : route
                  ? t(user.locale, navLabelKeys[route[1]] || route[1])
                  : "Pisto"}
            </strong>
            <small>{t(user.locale, "mobileWorkspace")}</small>
          </div>
          {widgetKeys.length > 0 && (
            <div className="header-widgets">
              {widgetKeys.map((k, i) => {
                const [label, value] = widget(k);
                return (
                  <div className="header-widget" key={k + "-" + i}>
                    <small>{label}</small>
                    <b>{value}</b>
                  </div>
                );
              })}
            </div>
          )}
          {debugEnabled && <span className="debug-id">{screenId}</span>}
          {loc.pathname === "/dashboard" && (
            <label className="dashboard-language" title={t(user.locale, "preferredLanguage")}>
              <Globe2 size={16} />
              <select
                value={String(user.locale || "en").slice(0, 2)}
                aria-label={t(user.locale, "preferredLanguage")}
                onChange={async (e) => {
                  await api("/preferences/language", {
                    method: "PUT",
                    body: { locale: e.target.value },
                  });
                  window.location.reload();
                }}
              >
                {supportedLocales.map(([code, name]) => (
                  <option value={code} key={code}>{name}</option>
                ))}
              </select>
            </label>
          )}
          <button
            className="icon top-eye"
            title={t(user.locale, hideValues ? "showMoney" : "hideMoney")}
            onClick={() => {
              const next = !hideValues;
              setHideValues(next);
              localStorage.setItem(
                "pisto-hide-values-runtime",
                next ? "1" : "0",
              );
              api("/preferences/visibility", {
                method: "PUT",
                body: { hidden: next },
              }).catch(() => {});
              window.dispatchEvent(new Event("pisto:visibility-changed"));
            }}
          >
            {hideValues ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
          <button className="top-add" onClick={() => openMovement()}>
            <PlusCircle size={17} />
            <span>{t(user.locale, "addMovement")}</span>
          </button>
        </header>
        <div className="page">
          {uiIssue && (
            <div className="ui-recovery">
              <span>Pisto recovered from an interface error: {uiIssue}</span>
              <button onClick={() => window.location.reload()}>
                Reload if needed
              </button>
            </div>
          )}
          <ErrorBoundary key={loc.pathname}>
            <Routes location={loc}>
              <Route
                path="/dashboard"
                element={<Dashboard user={user} onAddMovement={openMovement} />}
              />
              <Route path="/accounts" element={<Accounts />} />
              <Route
                path="/accounts/:id"
                element={
                  <AccountDetail user={user} onAddMovement={openMovement} />
                }
              />
              <Route
                path="/movements"
                element={<Transactions onAddMovement={openMovement} />}
              />
              <Route path="/review" element={<MatchReview />} />
              <Route path="/import" element={<ImportPage />} />
              <Route path="/organize" element={<Organize />} />
              <Route path="/rules" element={<RulesPage />} />
              <Route path="/planning" element={<Planning />} />
              <Route path="/subscriptions" element={<Subscriptions />} />
              <Route path="/investments" element={<Investments user={user} onAddMovement={openMovement} />} />
              <Route
                path="/config"
                element={<Config user={user} onDebugChange={setDebugEnabled} />}
              />
              <Route
                path="/audit"
                element={isAdmin ? <Audit /> : <Navigate to="/dashboard" replace />}
              />
              <Route path="/help" element={<Help />} />
              <Route
                path="/profile"
                element={<Profile user={user} onUserChange={setUser} />}
              />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </ErrorBoundary>
        </div>
      </main>
      <MovementModal
        open={movementOpen}
        context={movementContext}
        onClose={() => {
          setMovementOpen(false);
          setMovementContext({});
        }}
      />
    </div>
  );
}
function App() {
  const [status, setStatus] = useState(null),
    [user, setUser] = useState(undefined);
  const refresh = () =>
    api("/me")
      .then((x) => setUser(x.user))
      .catch(() => setUser(null));
  useEffect(() => {
    api("/setup/status")
      .then((s) => {
        setStatus(s);
        if (s.installed) refresh();
        else setUser(null);
      })
      .catch(() => setStatus({ installed: false }));
  }, []);
  if (!status || user === undefined)
    return (
      <div className="splash">
        <div className="brand big">
          <span>P</span>Pisto
        </div>
      </div>
    );
  if (!status.installed)
    return (
      <Setup
        done={() => {
          setStatus({ ...status, installed: true });
          refresh();
        }}
      />
    );
  if (!user) return <Login done={refresh} />;
  return <Shell user={user} setUser={setUser} />;
}
createRoot(document.getElementById("root")).render(
  <ErrorBoundary>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </ErrorBoundary>,
);
